import { http } from "@/lib/api";

export const MAX_PHOTOS_PER_VISIT = 12;
const MAX_SIDE = 2000;      // long edge in px: enough detail for X-rays, a fraction of a phone photo's size
const JPEG_QUALITY = 0.88;

const baseName = (name) => (name || "photo").replace(/\.[^.]+$/, "");

// Phone photos are 3-10 MB. Shrink to a sensible size before upload, and fall back to the
// original file for anything the browser can't decode (the server validates it either way).
export async function compressImage(file) {
  if (!file.type.startsWith("image/") || file.type === "image/gif") return file;
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY));
    if (!blob || (blob.size >= file.size && scale === 1)) return file;
    return new File([blob], `${baseName(file.name)}.jpg`, { type: "image/jpeg" });
  } catch {
    return file;
  }
}

export async function uploadVisitImages(visitId, files) {
  const failed = [];
  for (const f of files) {
    try {
      const body = new FormData();
      body.append("visit_id", visitId);
      body.append("file", await compressImage(f));
      await http.post("/visit-images", body);
    } catch (err) {
      failed.push({ name: f.name, error: err?.response?.data?.detail || err.message });
    }
  }
  return { uploaded: files.length - failed.length, failed };
}

export const listVisitImages = async (patientId) => (await http.get("/visit-images", { params: { patient_id: patientId } })).data;
export const deleteVisitImage = (id) => http.delete(`/visit-images/${id}`);
export const fetchImageBlob = async (storagePath) => (await http.get(`/files/${storagePath}`, { responseType: "blob" })).data;
